import React, { useEffect, useState } from "react";
import { Box, Button, TablePagination, Typography } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Loader from "components/loader";
import CategoryTabs from "components/category/CategoryTabs";
import ContainedButton from "components/button/contained-button";
import DeleteEditModal from "components/customModal/DeleteEditModal";
import { commonTableStyles as tableStyles } from "components/tables/styles";
import { ReducersModal, TestimonialData } from "utils/modals";
import { UPDATE_TESTIMONIAL_REDUCER } from "store/actionTypes";
import {
  SERVICE_CATEGORY,
  getServiceCategoryFromPathname,
  toServiceCategory,
} from "utils/serviceCategory";
import { CATEGORY_DATA } from "utils/constants";
import { isStoredMediaUrl, toDisplayTitle } from "utils/commonFunctions";
import ROUTE_NAMES from "routes/routeNames";
import { deleteTestimonial, getTestimonialList } from "./action";
import AddTestimonialModal from "./addTestimonial";
import { styles } from "./styles";

const PAGE_SIZE_OPTIONS = [6, 12, 24, 60];

const getCategoryLabel = (category: SERVICE_CATEGORY | number) => {
  const match = CATEGORY_DATA.find(
    (item) => Number(item.id) === toServiceCategory(category)
  );
  return match?.label || "Car Cleaning";
};

const Testimonial = () => {
  const dispatch: any = useDispatch();
  const {
    result = [],
    loader,
    category,
    page,
    limit,
    pagination,
    total,
  } = useSelector((state: ReducersModal) => state.TestimonialReducer);

  const location = useLocation();
  const pathCategory = getServiceCategoryFromPathname(location.pathname);

  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<TestimonialData | null>(null);
  const [playingCardId, setPlayingCardId] = useState<string | null>(null);

  const dispatchValue = (payload: any) => {
    dispatch({
      type: UPDATE_TESTIMONIAL_REDUCER,
      payload,
    });
  };

  useEffect(() => {
    const currentCategory = toServiceCategory(category);
    if (currentCategory !== pathCategory) {
      dispatchValue({ category: pathCategory, page: 1, result: [] });
      return;
    }
    dispatch(getTestimonialList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathCategory, category, page, limit]);

  const handleCategoryChange = (nextCategory: SERVICE_CATEGORY) => {
    if (nextCategory === toServiceCategory(category)) return;
    setPlayingCardId(null);
    dispatchValue({ category: nextCategory, page: 1, result: [] });
  };

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    dispatchValue({ page: newPage + 1 });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    dispatchValue({ limit: Number(event.target.value), page: 1 });
  };

  const handleAdd = () => {
    setSelected(null);
    setOpen(true);
  };

  const handleEdit = (item: TestimonialData) => {
    setSelected(item);
    setOpen(true);
  };

  const handleDelete = (item: TestimonialData) => {
    setSelected(item);
    setDeleteOpen(true);
  };

  const confirmDelete = () => {
    const id = selected?._id || selected?.id;
    const s3Urls = [selected?.thumbnail, selected?.media].filter((url) =>
      isStoredMediaUrl(url)
    ) as string[];
    if (id) dispatch(deleteTestimonial(id, () => {}, s3Urls));
    setDeleteOpen(false);
    setSelected(null);
    setPlayingCardId(null);
  };

  const renderCardMedia = (item: TestimonialData) => {
    const mediaUrl = item.mediaPreview || item.media || "";
    const thumbnailSrc = item.thumbnailPreview || item.thumbnail || "";
    const isPlaying = playingCardId === item._id;

    if (isPlaying && mediaUrl) {
      return (
        <video
          key={mediaUrl}
          src={mediaUrl}
          controls
          autoPlay
          playsInline
          preload="metadata"
          style={styles.thumbMedia}
        />
      );
    }

    return (
      <Box
        className="thumbnail-wrapper"
        sx={styles.thumbnailWrapper}
        onClick={() => setPlayingCardId(item._id)}
      >
        {thumbnailSrc ? (
          <img
            src={thumbnailSrc}
            alt={getCategoryLabel(item.category)}
            style={styles.thumbMediaCover}
          />
        ) : mediaUrl ? (
          <video
            src={mediaUrl}
            muted
            playsInline
            preload="metadata"
            style={styles.thumbMediaCover}
          />
        ) : (
          <Box sx={styles.mediaPlaceholder}>No media</Box>
        )}
        <Box sx={styles.playOverlay}>
          <Box sx={styles.playBtn}>
            <PlayArrowRoundedIcon />
          </Box>
        </Box>
      </Box>
    );
  };

  const renderGrid = () => {
    if (!loader && !result.length) {
      return (
        <Box sx={styles.grid}>
          <Box sx={styles.emptyState}>No testimonials available.</Box>
        </Box>
      );
    }

    return (
      <Box sx={styles.grid}>
        {result.map((item: TestimonialData) => {
          const cardTitle = getCategoryLabel(item.category);
          return (
          <Box key={item._id} sx={styles.card}>
            <Box sx={styles.thumb}>{renderCardMedia(item)}</Box>
            <Box sx={styles.cardBody}>
              <Typography sx={styles.cardTitle} title={cardTitle}>
                {cardTitle}
              </Typography>
            </Box>
            <Box sx={styles.cardFooter}>
              <Button
                sx={{ ...styles.cardAction, ...styles.editAction }}
                onClick={() => handleEdit(item)}
              >
                <EditOutlinedIcon fontSize="small" />
                Edit
              </Button>
              <Button
                sx={{ ...styles.cardAction, ...styles.deleteAction }}
                onClick={() => handleDelete(item)}
              >
                <DeleteOutlineIcon fontSize="small" />
                Delete
              </Button>
            </Box>
          </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ ...tableStyles.searchAndFilter, marginBottom: "16px" }}>
        <CategoryTabs
          basePath={ROUTE_NAMES.MAIN_ROUTES.TESTIMONIAL}
          onChange={handleCategoryChange}
        />
        <ContainedButton
          externalStyles={{
            ...tableStyles.HeadBtns,
            width: "auto",
            minWidth: "168px",
            whiteSpace: "nowrap",
          }}
          onClick={handleAdd}
          title="Add Testimonial"
        />
      </Box>

      <Box sx={{ ...tableStyles.paper, ...styles.listPaper }}>
        {renderGrid()}
        <Box sx={styles.pagination}>
          <TablePagination
            component="div"
            count={pagination?.totalRecords || total || 0}
            page={Math.max((page || 1) - 1, 0)}
            onPageChange={handleChangePage}
            rowsPerPage={limit || 6}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            sx={{ borderTop: "none", backgroundColor: "transparent" }}
          />
        </Box>
      </Box>

      <AddTestimonialModal
        key={open ? selected?._id || "new" : "closed"}
        state={open}
        setState={setOpen}
        selectedTestimonial={open ? selected : null}
      />

      <DeleteEditModal
        visible={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Confirmation"
      >
        {`Are you sure you want to delete "${
          toDisplayTitle(selected?.title) ||
          getCategoryLabel(selected?.category || 0)
        }"?`}
      </DeleteEditModal>

      {loader && <Loader />}
    </Box>
  );
};

export default Testimonial;
